import { useEffect, useState } from "react";
import { Activity, X, Loader2, Users, DollarSign, Eye, AlertCircle, Lock } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

/**
 * Newseze Pulse — "Explain this issue" modal.
 * Shows a 4-block plain-English brief: personal impact, financial impact,
 * who's affected, what to watch. Tier-gated: free sees only the personal hook.
 *
 * Usage:
 *   <PulseModal article={article} onClose={() => setShow(false)} onUpgrade={openPricing} />
 */
export default function PulseModal({ article, onClose, onUpgrade }) {
  const { token } = useAuth();
  const [pulse, setPulse] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true); setErr("");
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await http.get(`/pulse/${article.id}`, { headers });
        if (alive) setPulse(r.data);
      } catch (ex) {
        if (alive) setErr(ex?.response?.data?.detail || "Pulse is briefly unavailable.");
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [article.id, token]);

  return (
    <div
      className="fixed inset-0 bg-black/65 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="pulse-modal"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-[640px] w-full max-h-[92vh] sm:max-h-[88vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — sticky, full-width touch close target (44×44 min for iOS) */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#0a1f3d] to-[#1e3a8a] text-white p-4 sm:p-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="p-1.5 rounded-md bg-[var(--gold)] text-[#1d1303] shrink-0">
              <Activity size={16} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--gold)]">Newseze Pulse</div>
              <div className="text-[12.5px] font-bold opacity-90 truncate">Explain this issue</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-11 h-11 -mr-2 flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition"
            aria-label="Close"
            data-testid="pulse-close"
          >
            <X size={22} strokeWidth={2.5} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <h3 className="text-[18px] font-black tracking-[-0.01em] leading-tight mb-3" data-testid="pulse-title">
            {article.title}
          </h3>

          {busy && (
            <div className="flex items-center gap-2 text-[12.5px] text-[var(--muted)] py-6" data-testid="pulse-loading">
              <Loader2 size={14} className="animate-spin" /> Newseze is reading this story for you…
            </div>
          )}

          {err && !busy && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-[12.5px] text-rose-800 flex gap-2 items-start" data-testid="pulse-error">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          )}

          {pulse && !busy && (
            <div className="space-y-4">
              {pulse.summary && (
                <p className="text-[13.5px] font-bold text-[var(--ink)] border-l-4 border-[var(--gold)] pl-3 italic leading-snug" data-testid="pulse-summary">
                  {pulse.summary}
                </p>
              )}

              <PulseBlock icon={Eye} title="What it means for you" testid="pulse-personal" body={pulse.personal_impact} />

              {pulse.tier_gated ? (
                <UpgradeCard onUpgrade={onUpgrade} price={pulse.upgrade_price_usd} />
              ) : (
                <>
                  <PulseBlock icon={DollarSign} title="Financial impact" testid="pulse-financial" body={pulse.financial_impact} accent="emerald" />
                  {(pulse.who_is_affected || []).length > 0 && (
                    <div className="rounded-lg border border-[var(--line)] bg-white p-4" data-testid="pulse-who">
                      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-2">
                        <Users size={12} /> Who&apos;s affected
                      </div>
                      <ul className="space-y-1">
                        {pulse.who_is_affected.map((w, i) => (
                          <li key={i} className="text-[13px] flex items-start gap-2">
                            <span className="text-[var(--gold)] font-black">·</span>
                            <span>{w}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pulse.what_to_watch && (
                    <div className="rounded-lg bg-[#fff9ed] border border-[var(--gold)] p-3 text-[12.5px] leading-snug" data-testid="pulse-watch">
                      <span className="text-[10px] uppercase tracking-[0.18em] font-black text-[#7a5b00] block mb-1">What to watch next</span>
                      {pulse.what_to_watch}
                    </div>
                  )}
                </>
              )}

              <div className="text-[10.5px] text-[var(--muted)] italic pt-2 border-t border-[var(--line)]">
                Newseze Pulse uses our calm, evidence-based editorial filter. This is a plain-English summary, not financial or legal advice.
              </div>
            </div>
          )}

          {/* Bottom close — guaranteed exit affordance even after long scroll */}
          <div className="mt-4 pt-3 border-t border-[var(--line)] flex justify-end">
            <button onClick={onClose} className="btn" data-testid="pulse-close-bottom">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PulseBlock({ icon: Icon, title, body, testid, accent = "blue" }) {
  if (!body) return null;
  const accentClass = accent === "emerald" ? "text-emerald-700" : "text-[var(--blue)]";
  return (
    <div className="rounded-lg border border-[var(--line)] bg-white p-4" data-testid={testid}>
      <div className={`flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-black ${accentClass} mb-1.5`}>
        <Icon size={12} /> {title}
      </div>
      <p className="text-[13px] text-[var(--ink)] leading-snug">{body}</p>
    </div>
  );
}

function UpgradeCard({ onUpgrade, price = 6.99 }) {
  return (
    <div className="rounded-lg border-2 border-dashed border-[var(--gold)] bg-[#fff9ed] p-4 text-center" data-testid="pulse-upgrade">
      <Lock size={20} className="mx-auto text-[var(--gold)] mb-2" />
      <div className="text-[14px] font-black text-[var(--ink)] mb-1">Unlock the full Pulse</div>
      <p className="text-[12px] text-[var(--muted)] mb-3 leading-snug">
        Financial impact, who&apos;s affected, and what to watch — included with Reader (${price}/mo) and up.
      </p>
      <button onClick={onUpgrade} className="btn gold" data-testid="pulse-upgrade-btn">
        See plans
      </button>
    </div>
  );
}
